// Rebuild `agent.state.messages` for a chat this browser never ran.
//
// A restored chat renders from Postgres, but rendering is not continuing: the
// agent loop needs the conversation as the MODEL saw it, which the render
// documents deliberately do not carry. That history lives in the conversation
// store, as the newest invocation's input manifest, and this module is the one
// place that knows how to turn it back into pi-ai messages.
//
// -- Why the last reply comes from Postgres --------------------------------
// An input manifest is everything BEFORE the assistant's answer, so the union
// of what capture publishes is the whole conversation except its final
// assistant reply - nothing emits output manifests yet. That reply is
// therefore ALWAYS tail-filled from the persisted turns; it is the normal path,
// not a gap handler. Two further cases route through the same tail-fill:
// capture that has not landed (fail-open publication, broker outage, ingest
// lag), and a manifest the client or the normalizer trimmed.
//
// -- What this seam owns, and what it deliberately does not ----------------
// Everything here is derived from a shape another PR produces (the normalized
// turn, `normalized-turn-v1`: `{role, blocks:[...]}`), so it is kept thin,
// pure, and unit-tested against literal bodies. Nothing else in the dashboard
// parses that shape.
//
// Two losses are expected and documented rather than fixed here:
//   * **thinking is stripped.** The normalizer keeps thinking text but drops
//     its provider signature, and an unsigned thinking block is only accepted
//     mid-tool-loop - a resume point this product never uses, since every
//     resume is a run boundary. Replaying the text without the signature would
//     be a 400 waiting to happen for no gain.
//   * **image bytes are dropped** by the normalizer (a block with no source).
//     The persisted user turns still hold what the user pasted, so those are
//     patched back in from the transcript; an image with no match becomes a
//     text note rather than an empty block that claims to be an image.
//
// Dashboard notifications are NOT a third loss. Capture publishes them under
// their own role, so the type is a fact on the wire rather than something
// recovered by matching a marker inside user text. Nothing here parses
// `<dashboard_context>`: that wrapper belongs to `convertToLlm`, which adds it
// on the way OUT to a provider, and it is last-mile prompt syntax rather than
// how Origin stores a message type.

import { setTranscript } from "./agentTranscript.js";
import { recordEvictedMarker } from "./toolResultStore.js";

// Where a reconstruction's history came from. Surfaced so the caller can log or
// display a degraded continuation instead of guessing at one.
export const CONTINUATION_SOURCE = {
    /// Conversation store supplied the history; the tail came from Postgres.
    TYPED: "typed",
    /// Nothing in the conversation store - the whole history is synthesized
    /// from the persisted transcript.
    TRANSCRIPT: "transcript",
};

// -- normalized turn -> pi-ai messages -------------------------------------

function textOf(block) {
    return typeof block?.text === "string" ? block.text : "";
}

// One normalized tool_result block's `content` back into pi content blocks.
// The normalizer stores the provider's value verbatim, which is a string for
// most providers and a block list for Anthropic - anything else is stringified
// rather than dropped, because a tool result the model reasoned about must not
// silently become empty.
function toolResultContent(content) {
    if (typeof content === "string") return [{ type: "text", text: content }];
    if (Array.isArray(content)) {
        const blocks = content
            .filter((c) => c && typeof c === "object" && typeof c.text === "string")
            .map((c) => ({ type: "text", text: c.text }));
        if (blocks.length) return blocks;
    }
    if (content == null) return [{ type: "text", text: "" }];
    try {
        return [{ type: "text", text: JSON.stringify(content) }];
    } catch {
        return [{ type: "text", text: String(content) }];
    }
}

// Assistant-side blocks. `thinking` is dropped (see the module header);
// `tool_use` becomes pi's `toolCall`, whose args live under `arguments`.
function assistantBlocks(blocks) {
    const out = [];
    for (const block of blocks) {
        if (!block || typeof block !== "object") continue;
        if (block.type === "text") {
            const text = textOf(block);
            if (text) out.push({ type: "text", text });
        } else if (block.type === "tool_use") {
            out.push({
                type: "toolCall",
                id: block.id || "",
                name: block.name || "",
                arguments: block.input ?? {},
            });
        }
    }
    return out;
}

// User-side blocks, split at every tool_result: pi carries a tool result as its
// own `toolResult` message, while the provider shape nests it in a user turn.
// Walking in order and flushing keeps toolCall/toolResult adjacency, which the
// agent loop requires.
function userMessages(blocks) {
    const out = [];
    let pending = [];
    const flush = () => {
        if (!pending.length) return;
        out.push(userMessageFromBlocks(pending));
        pending = [];
    };
    for (const block of blocks) {
        if (!block || typeof block !== "object") continue;
        if (block.type === "tool_result") {
            flush();
            out.push({
                role: "toolResult",
                toolCallId: block.tool_use_id || "",
                toolName: block.tool_name || undefined,
                content: toolResultContent(block.content),
                isError: !!block.is_error,
            });
            continue;
        }
        pending.push(block);
    }
    flush();
    return out.filter(Boolean);
}

// A user turn's non-tool blocks. Text is text here - a user who happens to type
// something that looks like dashboard markup is a user typing markup.
function userMessageFromBlocks(blocks) {
    const content = [];
    for (const block of blocks) {
        if (block.type === "text") {
            const text = textOf(block);
            if (text) content.push({ type: "text", text });
        } else if (block.type === "image") {
            // Placeholder: the normalizer dropped the bytes. patchImages
            // replaces it from the transcript, or degrades it to a note.
            content.push({ type: "image", data: "", mimeType: "" });
        }
    }
    if (!content.length) return null;
    return { role: "user", content };
}

// One normalized turn -> zero or more pi messages.
//
// A `system` turn is dropped deliberately: pi rebuilds the system prompt on
// every send from the LIVE tenant analytics schema, so replaying the captured
// one would both duplicate it and pin the model to a stale schema.
export function normalizedTurnToMessages(turn) {
    if (!turn || typeof turn !== "object") return [];
    const blocks = Array.isArray(turn.blocks) ? turn.blocks : [];
    switch (turn.role) {
        // Capture publishes a dashboard notification under its own role, so the
        // type is read off the contract rather than recovered by matching a
        // marker inside user text.
        case "notification": {
            const text = blocks
                .filter((block) => block?.type === "text")
                .map(textOf)
                .join("");
            return text ? [{ role: "notification", text }] : [];
        }
        case "assistant": {
            const content = assistantBlocks(blocks);
            return content.length ? [{ role: "assistant", content }] : [];
        }
        case "user":
        case "tool":
            return userMessages(blocks);
        default:
            return [];
    }
}

// Parse one manifest item's opaque body. A body this build cannot read is
// skipped rather than thrown on: a single unreadable turn must not cost the
// user a continuable chat.
function parseTurn(item) {
    if (!item?.bodyJson) return null;
    try {
        const parsed = JSON.parse(item.bodyJson);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}

// The manifest, ordinal-ascending, as pi messages. Returns the messages plus
// the text of the NEWEST text-bearing user turn, which is the transcript
// alignment anchor.
//
// Newest text-bearing user turn rather than the request-driving (`current`) one:
// mid-tool-loop, the newest `current` turn is the tool result, whose blocks
// carry no text, so keying on `current` yielded no anchor at all and every
// continuation taken inside a tool loop degraded to the positional fallback.
// The prompt that opened the loop is still in the manifest, and it is the right
// join point - the tail-fill replays only assistant TEXT (assistantTextFromTurn
// drops tool calls), which is exactly the part an input manifest stops short of.
//
// Sorts by ordinal rather than trusting the arrival order, even though the
// server already does. Order is not a nicety here: a scrambled manifest
// separates every `toolCall` from its `toolResult`, which providers reject
// outright, and the wreckage reads like a reconstruction bug rather than a
// transport one. The cost is a sort of at most a few dozen items; the
// alternative is a silent, total corruption of the conversation whenever
// anything upstream reorders.
export function messagesFromManifest(turns) {
    const messages = [];
    let currentUserText = null;
    const ordered = (Array.isArray(turns) ? [...turns] : [])
        .sort((a, b) => (Number(a?.ordinal) || 0) - (Number(b?.ordinal) || 0));
    for (const item of ordered) {
        const turn = parseTurn(item);
        if (!turn) continue;
        const mapped = normalizedTurnToMessages(turn);
        if (turn.role === "user") {
            // An empty anchor is NOT an anchor. A user turn can be text-free -
            // an image-only prompt, or one carrying nothing but a tool result -
            // and matching "" would silently anchor on whatever text-free prompt
            // happens to sit nearest the transcript's end. Skipping those is
            // also what keeps "newest user turn" meaning "newest real prompt".
            const text = promptText(mapped);
            if (text) currentUserText = text;
        }
        messages.push(...mapped);
    }
    return { messages, currentUserText };
}

// The prompt text of a mapped user turn, for anchor comparison only.
//
// ONLY `user` messages count, because the anchor is matched against persisted
// `user` ITEMS and nothing else can ever legitimately match one. Two roles the
// provider's format also carries inside a user turn are therefore excluded, each
// for the same reason and with a different failure if it were not:
//   * `toolResult` - a tool result IS a user-role turn on the wire, so a
//     text-bearing one would supply an anchor made of tool OUTPUT.
//   * `notification` - dashboard context the model was shown, never something
//     the user typed, so it is never persisted as a user item.
// Either one appearing after the real prompt would SHADOW it (this scans to the
// newest match), leaving no anchor and silently degrading every continuation
// taken at that point to the positional fallback.
function promptText(messages) {
    return plainText(messages.filter((m) => m?.role === "user"));
}

// Concatenated text of a message list, for anchor comparison only.
function plainText(messages) {
    const parts = [];
    for (const message of messages) {
        if (!Array.isArray(message.content)) continue;
        for (const block of message.content) {
            if (block.type === "text") parts.push(block.text);
        }
    }
    return parts.join("").trim();
}

// -- transcript (Postgres) -> pi-ai messages -------------------------------

// A persisted user item's prompt text.
function itemText(item) {
    return typeof item?.text === "string" ? item.text : "";
}

// Find where the reconstruction stops inside the persisted transcript, so the
// tail can be filled without duplicating what the manifest already covered.
//
// Returns the index of the LAST top-level `user` item whose text matches the
// manifest's newest text-bearing user turn. When there is no anchor to match -
// capture never landed, or it is far enough behind that its newest prompt is not
// the transcript's - it falls back to the transcript's own last user item, and
// the caller treats the result as degraded.
//
// A transcript with NO user item at all yields the end of the list, i.e. "add
// nothing". There is no prompt to join on, so the alternative is appending the
// whole transcript behind a manifest that may already contain it - and the
// model reading its own words twice is a worse failure than the model not
// recalling its last sentence.
//
// Exported for its own tests: this is the one place a capture gap turns into
// visible loss or visible duplication.
export function alignTailStart(items, anchorText) {
    const rows = Array.isArray(items) ? items : [];
    let lastUser = -1;
    for (let i = rows.length - 1; i >= 0; i--) {
        if (rows[i]?.kind !== "user") continue;
        if (lastUser < 0) lastUser = i;
        if (anchorText != null && itemText(rows[i]).trim() === anchorText) {
            return { index: i, matched: true };
        }
    }
    return { index: lastUser < 0 ? rows.length - 1 : lastUser, matched: false };
}

// The trailing assistant text of one persisted turn item.
//
// Tool calls in the turn are deliberately NOT replayed. Every tool call except
// a run's last is already covered - the next LLM call's request carries it and
// its result - and a trailing one only exists when the run was interrupted,
// where replaying a `toolCall` with no matching `toolResult` would leave a
// dangling pair the provider rejects. Text is the whole of what this needs to
// recover.
function assistantTextFromTurn(item) {
    const children = Array.isArray(item?.children) ? item.children : [];
    const text = children
        .filter((child) => child?.kind === "assistant" && typeof child.text === "string")
        .map((child) => child.text)
        .join("");
    return text.trim() ? [{ role: "assistant", content: [{ type: "text", text }] }] : [];
}

// Persisted items from `startIndex` (exclusive) onward, as pi messages. Used
// both for the always-present final-reply tail-fill and, when the conversation
// store has nothing, for the whole history.
export function messagesFromItems(items, startIndex = -1) {
    const rows = Array.isArray(items) ? items : [];
    const out = [];
    for (let i = startIndex + 1; i < rows.length; i++) {
        const item = rows[i];
        if (!item || typeof item !== "object") continue;
        if (item.kind === "user") {
            const content = userContentFromItem(item);
            if (content.length) out.push({ role: "user", content });
        } else if (item.kind === "turn") {
            out.push(...assistantTextFromTurn(item));
        }
        // `system` / `kernel` / `audit` items are dashboard-local surfaces the
        // model was never shown; they carry no conversation.
    }
    return out;
}

function userContentFromItem(item) {
    const content = [];
    const text = itemText(item);
    if (text) content.push({ type: "text", text });
    for (const image of Array.isArray(item.images) ? item.images : []) {
        if (image?.data) content.push({ type: "image", data: image.data, mimeType: image.mimeType || "" });
    }
    return content;
}

// -- image patching --------------------------------------------------------

// Put the pasted-image bytes the normalizer dropped back onto the reconstructed
// user messages, matching a message to its persisted item by prompt text.
//
// A placeholder with no match degrades to a text note rather than staying an
// empty image block: an empty block is a malformed request to some providers,
// and silently dropping it would let the model answer about an image it was
// never shown as though it had seen one.
export function patchImages(messages, items) {
    const byText = new Map();
    for (const item of Array.isArray(items) ? items : []) {
        if (item?.kind !== "user" || !Array.isArray(item.images) || !item.images.length) continue;
        byText.set(itemText(item).trim(), item.images);
    }
    return messages.map((message) => {
        if (message.role !== "user" || !Array.isArray(message.content)) return message;
        if (!message.content.some((block) => block.type === "image")) return message;
        const key = message.content
            .filter((block) => block.type === "text")
            .map((block) => block.text)
            .join("")
            .trim();
        const images = byText.get(key) || [];
        let next = 0;
        const content = message.content.map((block) => {
            if (block.type !== "image") return block;
            const image = images[next++];
            return image?.data
                ? { type: "image", data: image.data, mimeType: image.mimeType || "" }
                : { type: "text", text: "[image from an earlier device is unavailable]" };
        });
        return { ...message, content };
    });
}

// -- tool-result store -----------------------------------------------------

// Mark every tool call in the reconstructed history as "this used to exist".
//
// The row payloads behind `from_tool_calls` / `expand_preview` are local cache
// and never left the original browser, so on a fresh device every referenced
// id would otherwise look like it was never recorded at all - an error the
// model cannot act on. An eviction marker is the shape the store already
// understands: consumers report the gap honestly, and the existing rehydrator
// re-runs the original `analytics_query` spec against current data on demand.
// That is the "live-and-disclosed" half of the fidelity promise.
//
// Existing entries are left alone, so this is safe to run over a chat whose
// results are already present locally.
export function seedPartialToolResults(namespaceKey, messages) {
    const seeded = [];
    for (const message of Array.isArray(messages) ? messages : []) {
        if (message?.role !== "assistant" || !Array.isArray(message.content)) continue;
        for (const block of message.content) {
            if (block?.type !== "toolCall" || !block.id) continue;
            if (recordEvictedMarker(namespaceKey, block.id, block.name)) seeded.push(block.id);
        }
    }
    return seeded;
}

// -- the whole reconstruction ----------------------------------------------

// Does this chat need its agent messages rebuilt before it can be continued?
//
// True for exactly one shape: a chat that exists on the server, has a
// transcript, and has no agent messages locally - i.e. it was restored here
// rather than run here. A locally-run chat already holds the real history and
// must never be overwritten by a reconstruction; an empty chat has nothing to
// reconstruct. Pure and exported because getting this wrong in either direction
// is silent: too eager clobbers live context, too shy continues from nothing.
export function needsContinuationRebuild({
    persistenceOn,
    serverChatId,
    itemCount,
    agentMessageCount,
}) {
    return Boolean(persistenceOn) && Boolean(serverChatId) && itemCount > 0 && agentMessageCount === 0;
}

// Claim the one continuation rebuild a chat is owed, removing it from the
// pending set.
//
// The claim is deliberately taken BEFORE the caller awaits the read, and that
// ordering is the whole point: the rebuild is an async replacement of
// `agent.state.messages`, so a second send arriving while the first is in
// flight must not start its own. It would resolve against the same pre-rebuild
// transcript and overwrite the history the first one installed, silently
// dropping whatever the user had just sent. Returns whether this caller owns
// the rebuild.
export function claimContinuationRebuild(pending, id) {
    if (!pending?.has(id)) return false;
    pending.delete(id);
    return true;
}

// Rebuild the context and install it on an idle agent.
//
// Returns the reconstruction (so the caller can report a degraded one) or null
// when there was nothing to do. Replacing `state.messages` wholesale is safe
// only on an idle agent - the same assumption the fork and hydrate replays
// make - which is why this runs from the send path before a run starts, and
// never mid-run.
export async function installContinuationContext({
    agent,
    serverChatId,
    items,
    namespaceKey,
    deps,
}) {
    if (!agent?.state || !serverChatId) return null;
    const result = await loadContinuationContext({ serverChatId, items, namespaceKey, deps });
    // An empty reconstruction means neither store had anything usable. Leave
    // the agent's own (empty) history alone rather than assigning [] over it.
    if (!result.messages.length) return result;
    setTranscript(agent, result.messages);
    return result;
}

// Whether a reconstruction is worth telling someone about: it fell back to the
// transcript, it is a suffix of what the model saw, or its tail joined without
// a matching prompt. The product decision is that none of these interrupt the
// user - a fresh-device continue after a capture gap tail-fills silently - so
// this drives a log line, not UI.
export function continuationIsDegraded(result) {
    return Boolean(
        result
        && (result.source === CONTINUATION_SOURCE.TRANSCRIPT
            || result.truncated
            || !result.tailAligned),
    );
}

// Fetch the continuation context, rebuild the agent messages, and seed the
// tool-result markers - everything the caller needs before its first send.
//
// Never throws: a chat that refused to continue over an analytics read would be
// a worse product than one that continues with weaker memory, so a failed read
// degrades to the transcript-only reconstruction. Deps are injected
// (`{ getChatContinuation }`) so the whole path is testable without a network.
export async function loadContinuationContext({ serverChatId, items, namespaceKey, deps }) {
    let continuation = null;
    try {
        continuation = await deps.getChatContinuation(serverChatId);
    } catch (err) {
        // Reaching the conversation store is best-effort by design. A chat that
        // refused to continue over an analytics read would be a worse product
        // than one that continues with weaker memory, so the failure degrades
        // to the transcript-only reconstruction below.
        console.warn("[chatContinuation] continuation read failed:", err?.message || err);
    }
    const result = reconstructAgentMessages({ continuation, items });
    result.seededToolCallIds = seedPartialToolResults(namespaceKey, result.messages);
    return result;
}

// Rebuild a chat's agent messages from the conversation store plus its
// persisted transcript.
//
// `continuation` is `getChatContinuation`'s result, or null when the read
// failed. `items` is the restored transcript.
//
// Returns `{ messages, source, truncated, tailAligned }`:
//   * `source` - which store supplied the history.
//   * `truncated` - the conversation store's suffix flag; it covers source-reported omissions,
//     unresolved stored lineage/body references, and the response turn cap. The separate dropped
//     counts report only omissions the source quantified, so zero counts do not clear this flag.
//   * `tailAligned` - whether the tail-fill anchored on a matching prompt. False means capture is
//     behind and the tail was taken from the transcript's last prompt instead, which is a
//     best-effort join, not a proven one.
export function reconstructAgentMessages({ continuation, items }) {
    const turns = continuation?.turns;
    if (!continuation?.invocationFound || !Array.isArray(turns) || !turns.length) {
        return {
            messages: messagesFromItems(items),
            source: CONTINUATION_SOURCE.TRANSCRIPT,
            truncated: false,
            tailAligned: false,
        };
    }
    const { messages, currentUserText } = messagesFromManifest(turns);
    const { index, matched } = alignTailStart(items, currentUserText);
    const combined = [...messages, ...messagesFromItems(items, index)];
    return {
        messages: patchImages(combined, items),
        source: CONTINUATION_SOURCE.TYPED,
        truncated: !!continuation.truncated,
        tailAligned: matched,
    };
}
